import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import {Box, Typography, Theme} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'
import {getCancellationUrl} from 'utils/calcom'

export interface SessionProps {
  sessionIndex: string
  title: string
  subtitle: string
  description: string
  status:
    | 'completed'
    | 'locked'
    | 'readyForBooking'
    | 'notAttended'
    | 'upcoming'
    | 'skipped'
  isOptional: boolean
  showBookingButton: boolean
  enableLearnMore?: boolean
  colors?: any // Product line colors
  isLastItem?: boolean
  nextIsLocked?: boolean
  programSlug?: string
  appointment?: {
    id: string
    calcomBookingUid?: string | null
    meetingLink?: string | null
    canReschedule?: boolean | null
    canJoin?: boolean | null
  } | null
  onCancelRequested?: (cancelUrl: string) => void
}

const useStyles = makeStyles<{
  colors?: any
  isLastItem?: boolean
  nextIsLocked?: boolean
}>()((theme: Theme, {colors, isLastItem, nextIsLocked}) => ({
  sessionContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
    position: 'relative',

    ':before': {
      content: isLastItem ? undefined : "''",
      position: 'absolute',
      top: '0',
      left: '16px',
      bottom: '-16px',
      width: '0',
      borderLeftStyle: 'dashed',
      borderLeftWidth: '1px',
      borderLeftColor: nextIsLocked ? '#D6D8E0' : colors?.primary,
    },
  },
  lockIconContainer: {
    width: '32px',
    height: '32px',
    background: '#FFFFFF',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
    flexShrink: 0,
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    padding: '24px',
    background: '#FFFFFF',
    border: `1px solid ${colors?.secondary || '#E1F4F7'}`,
    borderRadius: '16px',
    alignItems: 'center',
    flex: 1,
  },
  imageColumn: {
    width: '78px',
    height: '78px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginBottom: 'auto',
  },
  centerColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statusLabel: {
    padding: '6px 16px 4px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    display: 'inline-block',
    marginBottom: '16px',
    width: 'fit-content',
  },
  sessionInfoContainer: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  separator: {
    color: '#D6D8E0',
    fontSize: '14px',
    fontWeight: 400,
  },
  sessionInfo: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '130%',
    letterSpacing: '0.15px',
    color: '#1F1F1F',
  },
  subtitle: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '12px',
    color: '#251F1F',
  },
  optionalBadge: {
    padding: '6px 16px 4px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    backgroundColor: '#FFF3EC',
    color: '#E97E19',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#251F1F',
    marginBottom: '4px',
  },
  description: {
    maxWidth: '520px',
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#251F1F',
  },
  buttonColumn: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    flexShrink: 0,
  },
  outlinedButton: {
    boxShadow: 'none !important',
    borderColor: `${colors?.primary || '#148199'} !important`,
    '&:hover': {
      boxShadow: 'none !important',
      borderColor: `${colors?.primary || '#148199'} !important`,
    },
    '&.Mui-disabled': {
      boxShadow: 'none !important',
      borderColor: '#D6D8E0 !important',
    },
  },
}))

function getStatusLabel(
  status:
    | 'completed'
    | 'locked'
    | 'readyForBooking'
    | 'notAttended'
    | 'upcoming'
    | 'skipped',
  t: (key: string) => string
): string | null {
  if (status === 'completed')
    return t('consultations_program_session_completed')
  if (status === 'upcoming') return t('consultations_program_session_upcoming')
  if (status === 'notAttended')
    return t('consultations_program_session_not_attended')
  if (status === 'skipped') return t('consultations_program_session_skipped')
  if (status === 'locked') return null
  return null
}

function getStatusColors(
  status:
    | 'completed'
    | 'locked'
    | 'readyForBooking'
    | 'notAttended'
    | 'upcoming'
    | 'skipped'
): {backgroundColor: string; textColor: string} {
  if (status === 'completed') {
    return {
      backgroundColor: '#D1FAE5',
      textColor: '#065F46',
    }
  }
  if (status === 'upcoming') {
    return {
      backgroundColor: '#E7EDF9',
      textColor: '#07378F',
    }
  }
  if (status === 'notAttended') {
    return {
      backgroundColor: '#FFF3EC',
      textColor: '#E97E19',
    }
  }
  if (status === 'skipped') {
    return {
      backgroundColor: '#F3F4F6',
      textColor: '#6B7280',
    }
  }
  if (status === 'locked') {
    return {
      backgroundColor: '#FEF3C7',
      textColor: '#D97706',
    }
  }
  return {
    backgroundColor: '#E7EDF9',
    textColor: '#666666',
  }
}

const Session: React.FC<SessionProps> = ({
  sessionIndex,
  title,
  subtitle,
  description,
  status,
  isOptional,
  showBookingButton,
  enableLearnMore,
  isLastItem,
  colors,
  nextIsLocked,
  programSlug,
  appointment,
  onCancelRequested,
}) => {
  const {classes} = useStyles({colors, isLastItem, nextIsLocked})
  const {t} = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const cancellationUrl = appointment?.calcomBookingUid
    ? getCancellationUrl(appointment.calcomBookingUid)
    : null

  const handleLearnMoreClick = () => {
    history.push(`/consultations/program/${programSlug}/info/${sessionIndex}`, {
      referrer: location.pathname,
    })
  }

  const handleBookSessionClick = () => {
    history.push(
      `/consultations/program/${programSlug}/book?session=${sessionIndex}`
    )
  }

  const handleRescheduleClick = () => {
    if (appointment?.calcomBookingUid) {
      history.push(
        `/consultations/program/${programSlug}/book?session=${sessionIndex}&rescheduleUid=${appointment.calcomBookingUid}`
      )
    }
  }

  const handleJoinMeetingClick = () => {
    if (appointment?.meetingLink) {
      window.open(appointment.meetingLink, '_blank')
    }
  }

  const handleCancelClick = () => {
    if (!cancellationUrl) {
      return
    }
    if (onCancelRequested) {
      onCancelRequested(cancellationUrl)
    }
  }

  const statusLabel = getStatusLabel(status, t)
  const statusColors = getStatusColors(status)

  const renderButtons = () => {
    if (status === 'upcoming') {
      return (
        <>
          <PGButton
            variant='text'
            label={t('common_cancel')}
            onClick={handleCancelClick}
            disabled={!cancellationUrl}
            size='medium'
            className={classes.outlinedButton}
          />
          <PGButton
            variant='text'
            label={t('consultations_program_fertility_session_reschedule')}
            onClick={handleRescheduleClick}
            disabled={
              !appointment?.canReschedule || !appointment?.calcomBookingUid
            }
            size='medium'
          />

          <PGButton
            variant='outlined'
            label={t('consultations_program_fertility_session_join_meeting')}
            className={classes.outlinedButton}
            onClick={handleJoinMeetingClick}
            disabled={!appointment?.canJoin || !appointment?.meetingLink}
            size='medium'
          />
        </>
      )
    }

    if (status === 'readyForBooking') {
      return (
        showBookingButton && (
          <PGButton
            variant='outlined'
            label={t('consultations_program_session_book_session')}
            onClick={handleBookSessionClick}
            size='medium'
            className={classes.outlinedButton}
          />
        )
      )
    }

    if (status === 'locked') {
      const shouldEnableLearnMore = enableLearnMore ?? true

      return shouldEnableLearnMore ? (
        <PGButton
          variant='text'
          label={t('common_learn_more')}
          onClick={handleLearnMoreClick}
          size='medium'
        />
      ) : null
    }

    return null
  }

  return (
    <Box className={classes.sessionContainer}>
      {/* Lock Icon */}
      <Box
        className={classes.lockIconContainer}
        sx={{
          borderColor: status === 'locked' ? '#CCCCCC' : colors.primary,
        }}
      >
        {status === 'locked' ? (
          <LockIcon sx={{fontSize: '16px', color: '#CCCCCC'}} />
        ) : (
          <LockOpenIcon sx={{fontSize: '16px', color: colors.primary}} />
        )}
      </Box>

      {/* Session Card */}
      <Box className={classes.card}>
        {/* Image Column */}
        <Box className={classes.imageColumn}>
          <svg
            width='78'
            height='78'
            viewBox='0 0 78 78'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g clipPath='url(#clip0_8416_18877)'>
              <path
                d='M0 8C0 3.58172 3.58172 0 8 0H70C74.4183 0 78 3.58172 78 8V70C78 74.4183 74.4183 78 70 78H8C3.58172 78 0 74.4183 0 70V8Z'
                fill={status === 'locked' ? '#FAFAFA' : colors.secondary}
              />
              <circle
                cx='1.10985'
                cy='79.3874'
                r='46.9451'
                stroke={status === 'locked' ? '#CBCCD6' : colors.primary}
                strokeWidth='15.7083'
                opacity={0.2}
              />
              <circle
                cx='8.72899'
                cy='66.3262'
                r='46.9451'
                stroke={status === 'locked' ? '#CBCCD6' : colors.primary}
                strokeWidth='15.7083'
              />
            </g>
            <defs>
              <clipPath id='clip0_8416_18877'>
                <path
                  d='M0 8C0 3.58172 3.58172 0 8 0H70C74.4183 0 78 3.58172 78 8V70C78 74.4183 74.4183 78 70 78H8C3.58172 78 0 74.4183 0 70V8Z'
                  fill='white'
                />
              </clipPath>
            </defs>
          </svg>
        </Box>

        {/* Center Column */}
        <Box className={classes.centerColumn}>
          {statusLabel && (
            <Typography
              className={classes.statusLabel}
              sx={{
                backgroundColor: statusColors.backgroundColor,
                color: statusColors.textColor,
              }}
            >
              {statusLabel}
            </Typography>
          )}

          <div className={classes.sessionInfoContainer}>
            <Typography className={classes.sessionInfo}>
              {t('common_session')} {sessionIndex}
            </Typography>

            <Typography className={classes.separator}>|</Typography>

            <Typography className={classes.subtitle}>{subtitle}</Typography>

            {isOptional && (
              <Typography className={classes.optionalBadge}>
                {t('consultations_program_session_optional')}
              </Typography>
            )}
          </div>

          <Typography className={classes.title}>{title}</Typography>

          <Typography className={classes.description}>{description}</Typography>
        </Box>

        {/* Button Column */}
        <Box className={classes.buttonColumn}>{renderButtons()}</Box>
      </Box>
    </Box>
  )
}

export default Session
